'use client';
// Incertitude de mesure — ISO 15189:2022 §7.3.2.
//
// La norme demande d'évaluer l'incertitude de chaque grandeur mesurée, de la
// réviser périodiquement, et de la mettre à disposition sur demande. Le produit
// ne la calculait ni ne la conservait nulle part.
//
// ── Cinq choses que cet écran refuse de faire ───────────────────────────────
//
//  · ACCEPTER UNE SAISIE. Il n'existe aucun champ où taper une incertitude.
//    Tout vient des points de contrôle interne, des valeurs assignées des lots
//    et de la chaîne de raccordement. Une incertitude ressaisie serait fausse
//    dès la première dérive, et invérifiable.
//
//  · MASQUER CE QU'IL NE SAIT PAS CALCULER. Les cartes sans incertitude sont
//    listées comme les autres, avec ce qui leur manque, nommément. Un tableau
//    qui n'afficherait que les incertitudes existantes se lirait « tout est
//    couvert ».
//
//  · CONFONDRE « partielle » ET « complète ». Une composante absente n'est pas
//    une composante nulle : le chiffre est alors nécessairement SOUS-ÉVALUÉ, et
//    il porte la mention. Publier une incertitude sous-évaluée comme complète
//    ferait rendre un résultat comme sûr alors qu'il ne l'est pas.
//
//  · CHOISIR LE SEUIL. Le rattachement d'un niveau de contrôle à une colonne du
//    référentiel (Bas / Moyen / Élevé) détermine contre quoi la méthode est
//    jugée. L'écran le PROPOSE, signale quand la cible tombe à mi-chemin, et
//    attend une confirmation. Sans elle : aucun verdict.
//
//  · MONTRER UN CHIFFRE SANS SON CALCUL. Chaque niveau ouvre le détail des
//    lots, des points exclus et de chaque composante avec sa formule et sa
//    provenance. C'est ce qu'un auditeur demande — pas le chiffre.
import React, { useEffect, useMemo, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { Tableau } from '@/shared/Tableau';
import { BoutonJournal } from '@/components/BoutonJournal';
import { fmtDate } from '@/lib/meta';
import {
  I, card, inputStyle, EmptyState, FilterBar, RefISO,
  FieldError, useBusy, usePagination, Pagination } from '@/components/ui';
import { H1, lbl, pill } from './commun';
import { Norme } from '@/components/Norme';
import type {
  GedIncDossier, GedIncDetail, GedIncLigne, GedIncNiveau, GedIncComposante,
  GedIncVerdict, GedIncCouverture, GedIncEvaluation,
} from '@/lib/types';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
const encart: React.CSSProperties = {
  background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)',
  borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5,
  color: 'var(--text-body)', lineHeight: 1.5,
};
const mono: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 12.5 };

const NB = (v: number | null | undefined, d = 2) =>
  (v === null || v === undefined ? '—' : v.toFixed(d).replace('.', ','));

const VERDICT_STYLE: Record<GedIncVerdict, { bg: string; fg: string; label: string }> = {
  conforme: { bg: 'var(--green-50)', fg: 'var(--green-600)', label: 'Conforme' },
  a_surveiller: { bg: 'var(--amber-50)', fg: 'var(--amber-600)', label: 'À surveiller' },
  non_conforme: { bg: 'var(--red-50)', fg: 'var(--red-600)', label: 'Non conforme' },
  sans_verdict: { bg: 'var(--gray-100)', fg: 'var(--text-muted)', label: 'Sans verdict' },
};
const COUV_STYLE: Record<GedIncCouverture, { bg: string; fg: string; label: string }> = {
  complete: { bg: 'var(--green-50)', fg: 'var(--green-600)', label: 'Complète' },
  partielle: { bg: 'var(--cyan-50)', fg: 'var(--cyan-700)', label: 'Partielle' },
  aucune: { bg: 'var(--gray-100)', fg: 'var(--text-muted)', label: 'Aucune' },
};

const Verdict = ({ v }: { v: GedIncVerdict }) => pill(VERDICT_STYLE[v] || VERDICT_STYLE.sans_verdict);
const Couv = ({ c }: { c: GedIncCouverture }) => pill(COUV_STYLE[c] || COUV_STYLE.aucune);

/** Un compteur du bandeau de bilan. Chiffrer la couverture est la moitié de la
 *  réponse à l'auditeur : « combien de vos examens ont une incertitude, et pour
 *  les autres, pourquoi pas ? ». */
function Tuile({ n, label, aide, ton }: { n: number; label: string; aide: string; ton?: 'sourd' | 'alerte' }) {
  return (
    <div title={aide} style={{
      ...card, padding: '10px 13px', display: 'grid', gap: 2, cursor: 'help', minWidth: 110,
    }}>
      <span style={{
        fontSize: 22, fontWeight: 700, lineHeight: 1.1,
        color: ton === 'alerte' ? 'var(--red-600)' : ton === 'sourd' ? 'var(--text-muted)' : 'var(--text-strong)',
      }}>{n}</span>
      <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>
        {label}
      </span>
    </div>
  );
}

/** Une composante, avec sa formule, sa provenance — et son MOTIF quand elle
 *  manque. C'est la ligne que l'écran refuse de rendre muette : « — » sans
 *  explication se lirait « négligeable », ce qui est une affirmation que la
 *  donnée ne porte pas. */
function Composante({ c }: { c: GedIncComposante }) {
  return (
    <div style={{
      padding: '8px 11px', borderTop: '1px solid var(--border-subtle)',
      display: 'grid', gap: 3,
      background: c.disponible ? 'transparent' : 'var(--gray-25)',
    }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 12.5, color: 'var(--text-strong)' }}>{c.libelle}</strong>
        <span style={{ ...mono, color: 'var(--text-muted)' }}>{c.formule}</span>
        <span style={{ flex: 1 }} />
        {c.disponible ? (
          <span style={{ ...mono, fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>
            {NB(c.valeurPct, 3)} %
          </span>
        ) : (
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)' }}>
            non calculable
          </span>
        )}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>{c.source}</div>
      {!c.disponible && c.motif && (
        <div style={{ fontSize: 12.5, color: 'var(--amber-600)', lineHeight: 1.5 }}>
          Ce qui manque : {c.motif}
        </div>
      )}
      {c.ecarteeVolontairement && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Écartée par décision de paramétrage — ce n’est pas une composante absente.
        </div>
      )}
    </div>
  );
}

/** Le détail d'un niveau : les lots, ce qui a été exclu, les composantes, le
 *  chiffre, et le rattachement au référentiel. Tout ce qui permet de REJOUER. */
function Niveau({ n, unite, sfbc, onSfbc, peut }: {
  n: GedIncNiveau; unite: string;
  sfbc: string; onSfbc: (v: string) => void; peut: boolean;
}) {
  const r = n.resultat;
  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <div style={{
        padding: '10px 13px', display: 'flex', gap: 12, alignItems: 'baseline',
        flexWrap: 'wrap', background: 'var(--gray-25)',
      }}>
        <strong style={{ fontSize: 13.5, color: 'var(--text-strong)' }}>{n.libelle}</strong>
        <Couv c={r.couverture} />
        <Verdict v={n.verdict} />
        <span style={{ flex: 1 }} />
        {r.uPct === null ? (
          <span style={{ fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 700 }}>
            aucune incertitude publiable
          </span>
        ) : (
          <span style={{ ...mono, fontSize: 15, fontWeight: 700, color: 'var(--text-strong)' }}>
            U = ± {NB(r.uPct, 2)} %
            {r.uAbsolue !== null && <span style={{ fontSize: 12.5, fontWeight: 400, color: 'var(--text-muted)' }}>
              {' '}(± {NB(r.uAbsolue, 3)}{unite ? ` ${unite}` : ''})
            </span>}
          </span>
        )}
      </div>

      {r.sousEvaluee && (
        <div style={{ ...encart, borderRadius: 0, borderInlineStart: 'none', borderBlockEnd: '1px solid var(--border-subtle)' }}>
          <strong style={{ color: 'var(--text-strong)' }}>Chiffre sous-évalué.</strong>{' '}
          Une composante manque ; elle n’a <em>pas</em> été remplacée par zéro, mais l’incertitude
          rendue est nécessairement plus petite que la vraie. À ne pas publier comme complète.
        </div>
      )}

      {/* Les entrées du calcul, lot par lot. */}
      <div style={{ padding: '9px 13px', display: 'grid', gap: 6 }}>
        <span style={lbl}>Entrées du calcul — {n.agregat.n} point(s) sur {n.agregat.nLotsRetenus} lot(s) retenu(s)</span>
        {n.lots.length === 0 ? (
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
            Aucun point de contrôle sur la fenêtre de référence.
          </p>
        ) : (
          <>
          <Tableau
            defilementHorizontal
            /* L6 (10/10/2026) : UNE clé pour tous les niveaux (décision L6) — les
               colonnes sont les mêmes à chaque niveau, et une préférence par
               niveau multipliait les réglages d'un même tableau. Niveau SIMPLE :
               un sous-tableau de fiche, tri seulement. */
            id="incertitude-lots"
            niveau="simple"
            lignes={n.lots}
            cleLigne={(l) => String(l.periodeId)}
            hauteurMax={320}
            colonnes={[
              { cle: 'lot', libelle: 'Lot', valeur: (l) => l.lotControle || '',
                rendu: (l) => l.lotControle || '—', style: mono },
              { cle: 'periode', libelle: 'Période', type: 'date',
                /* Le tri porte sur la DATE brute, pas sur la chaîne affichée :
                   « 03/09 » et « 12/08 » se rangent à l'envers en texte. */
                valeur: (l) => l.premierPassage || '',
                rendu: (l) => (l.premierPassage ? `${fmtDate(l.premierPassage)} → ${fmtDate(l.dernierPassage)}` : '—'),
                style: { color: 'var(--text-muted)' } },
              { cle: 'n', libelle: 'n', type: 'nombre', valeur: (l) => l.n, rendu: (l) => l.n, style: mono },
              { cle: 'moy', libelle: 'Moyenne', type: 'nombre', valeur: (l) => l.moyenne, rendu: (l) => NB(l.moyenne, 4), style: mono },
              { cle: 'sd', libelle: 'ET (n−1)', type: 'nombre', valeur: (l) => l.sd, rendu: (l) => NB(l.sd, 4), style: mono },
              { cle: 'cible', libelle: 'Valeur assignée', type: 'nombre', valeur: (l) => l.cible, rendu: (l) => NB(l.cible, 4), style: mono },
            ]}
          />
          {/* ⚠️ LE POOLÉ EST SORTI DU TABLEAU (25/08/2026). Il y était une
              rangée comme les autres ; dans un tableau TRIABLE, un clic sur
              une colonne l'aurait rangé au milieu des lots — un total au
              milieu de ses termes, que rien n'aurait signalé. Ce n'est pas
              une ligne de la liste, c'est sa conclusion : il se lit dessous. */}
          {n.lots.length > 1 && (
            <div style={{
              display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'baseline',
              marginTop: 6, paddingTop: 6, borderTop: '1.5px solid var(--border-default)',
              fontSize: 12.5,
            }}>
              <strong>Poolé</strong>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                variances combinées, {n.agregat.degresLiberte} ddl
              </span>
              <span style={{ flex: 1 }} />
              <span style={mono}>n {n.agregat.n}</span>
              <span style={mono}>moyenne {NB(n.agregat.moyenne, 4)}</span>
              <span style={mono}>ET {NB(n.agregat.sd, 4)}</span>
            </div>
          )}
          </>
        )}
        {n.lots.length > 1 && (
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Les <strong>variances</strong> des lots sont combinées, jamais les valeurs : un changement de
            lot déplace la moyenne, et mettre les points bout à bout ferait entrer ce décalage de
            consommable dans l’incertitude de la méthode.
          </p>
        )}
        {n.exclusions.length > 0 && (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Écartés :{' '}
            {n.exclusions.map((e) => `${e.n} ${e.motif}`).join(' · ')}.
          </div>
        )}
      </div>

      {/* Les composantes. */}
      <div>
        <div style={{ padding: '6px 13px 0' }}><span style={lbl}>Composantes</span></div>
        {n.composantes.map((c) => <Composante key={c.cle} c={c} />)}
        <div style={{
          padding: '8px 13px', borderTop: '1.5px solid var(--border-default)',
          display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap',
        }}>
          <strong style={{ fontSize: 12.5, color: 'var(--text-strong)' }}>Incertitude-type composée</strong>
          <span style={{ ...mono, color: 'var(--text-muted)' }}>u_c = √( Σ u_i² )</span>
          <span style={{ flex: 1 }} />
          <span style={{ ...mono, fontSize: 13, fontWeight: 700 }}>{NB(r.ucPct, 3)} %</span>
        </div>
      </div>

      {/* Le rattachement au référentiel — proposé, jamais décidé. */}
      <div style={{ padding: '9px 13px', borderTop: '1px solid var(--border-subtle)', display: 'grid', gap: 6 }}>
        <span style={lbl}>Seuil d’acceptabilité</span>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12.5, color: 'var(--text-body)' }} htmlFor={`sfbc-${n.niveau}`}>
            Colonne du référentiel
          </label>
          <select id={`sfbc-${n.niveau}`} value={sfbc} onChange={(e) => onSfbc(e.target.value)}
            disabled={!peut} style={{ ...inputStyle, width: 190, height: 32, fontSize: 12.5 }}>
            <option value="">— non confirmée —</option>
            <option value="bas">Bas</option>
            <option value="moyen">Moyen</option>
            <option value="eleve">Élevé</option>
          </select>
          {n.niveauSfbcPropose && (
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              proposé : <strong>{n.niveauSfbcPropose}</strong>
              {n.niveauSfbcAmbigu && (
                <span style={{ color: 'var(--amber-600)', fontWeight: 700 }}>
                  {' '}— la cible tombe à mi-chemin entre deux colonnes
                </span>
              )}
            </span>
          )}
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
            seuil retenu : <strong style={mono}>{n.seuilPct === null ? '—' : `${NB(n.seuilPct, 1)} %`}</strong>
          </span>
        </div>
        {n.motifSansSeuil ? (
          /* Arbitrage Q11, tranché le 07/08/2026. Le motif est SERVI par le
             moteur, pas reformulé ici : l'écran et le dossier imprimé doivent
             donner la même raison, mot pour mot. */
          <p style={{
            margin: 0, fontSize: 12.5, lineHeight: 1.5, color: 'var(--amber-700)',
            background: 'var(--amber-50)', border: '1px solid var(--amber-200)',
            borderRadius: 'var(--radius-sm)', padding: '7px 9px',
          }}>
            <strong>Sans verdict — rattachement ambigu.</strong> {n.motifSansSeuil}
          </p>
        ) : !n.niveauSfbcRetenu ? (
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Tant que le rattachement n’est pas confirmé, aucun verdict n’est rendu. Ce choix
            détermine contre quel seuil la méthode est jugée : il n’est pas pris par le logiciel.
          </p>
        ) : <span />}
      </div>
    </div>
  );
}

/** Une évaluation figée. Le chiffre vient de la ligne enregistrée ; le VERDICT
 *  est recalculé contre le référentiel d’aujourd’hui — et l’écran le dit. */
function Figee({ e }: { e: GedIncEvaluation }) {
  return (
    <div style={{ padding: '9px 12px', borderTop: '1px solid var(--border-subtle)', display: 'grid', gap: 5 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5 }}>
        <span style={{ ...mono, color: 'var(--text-muted)' }}>{e.ref}</span>
        {/* Règle RQ du 18/08, câblée le 24/08/2026. */}
        <BoutonJournal reference={e.ref} quoi="de cette évaluation" />
        <span style={{ color: 'var(--text-muted)' }}>{fmtDate(e.dateEvaluation)}</span>
        <Couv c={e.couverture} />
        <Verdict v={e.verdict} />
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>évaluée par {e.evalueParNom || '—'}</span>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
        {e.niveaux.map((n) => (
          <span key={n.niveau} style={{ marginInlineEnd: 14 }}>
            <strong>{n.libelle}</strong> : {n.uPct === null ? 'non calculable' : `U = ± ${NB(n.uPct, 2)} %`}
            {n.seuilPct !== null && <span style={{ color: 'var(--text-muted)' }}> · seuil {NB(n.seuilPct, 1)} %</span>}
          </span>
        ))}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Fenêtre {fmtDate(e.fenetreDebut)} → {fmtDate(e.fenetreFin)} · k = {e.parametres.k} ·
        biais « {e.parametres.traitementBiais} » · {e.motif}
        {e.sourceCritereRef && <> · critères du jour : {e.sourceCritereRef}</>}
        {/* Fragments imbriqués : le parseur du serveur de développement rejette
            `… : <>…</>}</>` en fin de ternaire — `next build` l'accepte, l'écran
            de développement rendait un 500 sans que rien d'autre ne le signale.
            Un <span> nommé lève l'ambiguïté sans changer le rendu. */}
        {e.revuePrevue && (
          <span> · révision {e.enRetard
            ? <strong style={{ color: 'var(--red-600)' }}>échue le {fmtDate(e.revuePrevue)}</strong>
            : <span>prévue le {fmtDate(e.revuePrevue)}</span>}</span>
        )}
      </div>
      {e.commentaire && <div style={{ fontSize: 12.5, color: 'var(--text-body)' }}>{e.commentaire}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Le détail d'une carte
 * ═══════════════════════════════════════════════════════════════════════════ */

function Detail({ id, onFerme, onEvalue }: { id: string; onFerme: () => void; onEvalue: () => void }) {
  const [d, setD] = useState<GedIncDetail | null>(null);
  const [erreur, setErreur] = useState('');
  const [tour, setTour] = useState(0);
  const [sfbc, setSfbc] = useState<Record<string, string>>({});
  const [motif, setMotif] = useState('periodique');
  const [commentaire, setCommentaire] = useState('');
  const [msg, setMsg] = useState('');
  const { run, estOccupe } = useBusy();

  useEffect(() => {
    let vivant = true;
    const q = Object.keys(sfbc).length ? `?niveaux=${encodeURIComponent(JSON.stringify(sfbc))}` : '';
    void fetch(`/api/ged/ged/incertitudes/carte/${id}${q}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('lecture refusée'))))
      .then((j: GedIncDetail) => { if (vivant) { setD(j); setErreur(''); } })
      .catch(() => { if (vivant) setErreur('Détail indisponible — le serveur n’a pas répondu. Ce n’est pas « aucune donnée ».'); });
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, tour, JSON.stringify(sfbc)]);

  const figer = () => run('figer', async () => {
    setMsg('');
    const r = await fetchEcriture(`/api/ged/ged/incertitudes/carte/${id}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ motif, commentaire, niveauSfbcRetenu: sfbc }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setMsg(j?.error?.message || 'Évaluation refusée.'); return; }
    setCommentaire('');
    setTour((t) => t + 1);
    onEvalue();
  });

  if (erreur) return <p style={{ fontSize: 13, color: 'var(--red-600)' }}>{erreur}</p>;
  if (!d) return <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Chargement du calcul…</p>;

  const c = d.carte;
  const calculable = c.niveaux.some((n) => n.resultat.couverture !== 'aucune');

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Bouton taille="sm" onClick={onFerme}>
          <I n="back" /> Retour à la couverture
        </Bouton>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-strong)' }}>
          {c.analyteCode} — {c.analyteNom}
        </h2>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          sur {c.equipementCode}{c.equipementNom ? ` (${c.equipementNom})` : ''}
          {c.matiere ? ` · ${c.matiere}` : ''}{c.unite ? ` · ${c.unite}` : ''}
        </span>
        <span style={{ flex: 1 }} />
        <Couv c={c.couverture} />
        <Verdict v={c.verdict} />
      </div>

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '86ch' }}>
        Fenêtre de référence <strong>{fmtDate(c.fenetreDebut)} → {fmtDate(c.fenetreFin)}</strong>
        {' '}({d.parametrage.fenetreMois} mois) · facteur d’élargissement <strong>k = {d.parametrage.k}</strong>
        {' '}· traitement du biais <strong>{d.parametrage.traitementBiais}</strong>
        {' '}· seuils lus dans la colonne <strong>{d.parametrage.familleCritere}</strong>
        {c.sourceCritere ? <> du référentiel <strong>{c.sourceCritere}</strong></> : null}.
      </p>

      {c.manques.length > 0 && (
        <div style={encart}>
          <strong style={{ color: 'var(--text-strong)' }}>Ce qui manque à cet examen</strong>
          <ul style={{ margin: '5px 0 0', paddingInlineStart: 18, display: 'grid', gap: 3 }}>
            {c.manques.map((m) => <li key={m}>{m}</li>)}
          </ul>
        </div>
      )}

      {c.niveaux.map((n) => (
        <Niveau key={n.niveau} n={n} unite={c.unite} peut={d.peutEvaluer}
          sfbc={sfbc[String(n.niveau)] ?? (n.niveauSfbcRetenu || '')}
          onSfbc={(v) => setSfbc((x) => ({
            ...Object.fromEntries(c.niveaux.map((y) => [String(y.niveau), x[String(y.niveau)] ?? (y.niveauSfbcRetenu || '')])),
            [String(n.niveau)]: v,
          }))} />
      ))}

      {d.peutEvaluer && (
        <div style={{ ...card, padding: 14, display: 'grid', gap: 10 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--text-strong)' }}>
            Figer cette évaluation
          </h3>
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '86ch' }}>
            Figer recopie les <strong>entrées</strong> du calcul — lots, effectifs, moyennes, écarts-types,
            valeurs assignées, dates — et le <strong>paramétrage</strong> en vigueur aujourd’hui. C’est ce
            qui permettra de rejouer ce chiffre à l’identique dans un an, quand les périodes de lot auront
            été closes. Aucune route ne réécrit une évaluation figée : réviser, c’est en prendre une
            nouvelle, et l’écart entre les deux est lui-même une information.
          </p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={lbl} htmlFor="inc-motif">Ce qui déclenche l’évaluation</label>
              <select id="inc-motif" value={motif} onChange={(e) => setMotif(e.target.value)}
                style={{ ...inputStyle, width: 300, height: 34, fontSize: 12.5 }}>
                {d.motifs.map((m) => <option key={m.cle} value={m.cle}>{m.libelle}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <label style={lbl} htmlFor="inc-com">Commentaire</label>
              <input id="inc-com" value={commentaire} onChange={(e) => setCommentaire(e.target.value)}
                style={{ ...inputStyle, height: 34, fontSize: 12.5 }} />
            </div>
            <Bouton variante="primaire" onClick={figer} disabled={!calculable || estOccupe('figer')}>
              <I n="check" /> Figer l’évaluation
            </Bouton>
          </div>
          {!calculable && (
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--amber-600)' }}>
              Aucun niveau n’a d’incertitude calculable sur la fenêtre : figer une évaluation vide
              reviendrait à publier un chiffre bâti sur du vide.
            </p>
          )}
          <FieldError>{msg}</FieldError>
        </div>
      )}

      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '10px 12px' }}>
          <span style={lbl}>Évaluations figées — {d.historique.length}</span>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            L’incertitude affichée est celle enregistrée ce jour-là. Le <strong>verdict</strong>, lui, est
            recalculé contre le référentiel de critères <em>en vigueur aujourd’hui</em> : il n’est stocké
            nulle part, sinon un dossier continuerait de dire « conforme » contre une limite révisée depuis.
          </p>
        </div>
        {d.historique.length === 0 ? (
          <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border-subtle)', fontSize: 12.5, color: 'var(--text-muted)' }}>
            Aucune évaluation n’a encore été figée pour cet examen. Le calcul ci-dessus existe, mais il
            n’est pas <strong>opposable</strong> : rien ne l’a daté ni signé.
          </div>
        ) : d.historique.map((e) => <Figee key={e.documentId} e={e} />)}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Paramétrage — les choix de laboratoire
 * ═══════════════════════════════════════════════════════════════════════════ */

function Parametrage({ d, onFait, onAnnule }: { d: GedIncDossier; onFait: () => void; onAnnule: () => void }) {
  const p = d.parametrage;
  const [v, setV] = useState({
    fenetreMois: String(p.fenetreMois), minPoints: String(p.minPoints), k: String(p.k),
    traitementBiais: p.traitementBiais, facteurLimite: String(p.facteurLimite),
    rattachementAmbigu: p.rattachementAmbigu,
    familleCritere: p.familleCritere, seuilSurveillancePct: String(p.seuilSurveillancePct),
    periodiciteRevueMois: String(p.periodiciteRevueMois), kEtalon: String(p.kEtalon),
  });
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();

  const enregistrer = () => run('p', async () => {
    setErreur('');
    const r = await fetchEcriture('/api/ged/ged/incertitudes/parametrage', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...v,
        fenetreMois: Number(v.fenetreMois), minPoints: Number(v.minPoints), k: Number(v.k),
        facteurLimite: Number(v.facteurLimite), rattachementAmbigu: v.rattachementAmbigu,
        seuilSurveillancePct: Number(v.seuilSurveillancePct),
        periodiciteRevueMois: Number(v.periodiciteRevueMois), kEtalon: Number(v.kEtalon),
      }),
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      setErreur(j?.error?.message || 'Enregistrement refusé.');
      return;
    }
    onFait();
  });

  const nombre = (cle: keyof typeof v, label: string, aide: string, pas = '1') => (
    <div>
      <label style={lbl} htmlFor={`p-${cle}`}>{label}</label>
      <input id={`p-${cle}`} type="number" step={pas} value={v[cle]}
        onChange={(e) => setV((x) => ({ ...x, [cle]: e.target.value }))} style={inputStyle} />
      <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>{aide}</p>
    </div>
  );

  return (
    <div style={{ ...card, padding: 16, display: 'grid', gap: 14 }}>
      <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)' }}>
        Mode de calcul de l’incertitude
      </h2>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '86ch' }}>
        Le mode de calcul, la période de référence, le facteur d’élargissement et le traitement du biais
        sont des <strong>choix de laboratoire</strong> : c’est le responsable qualité qui les défendra en
        audit, pas le logiciel. Les valeurs ci-dessous partent de celles du dossier de vérification du
        laboratoire lui-même. Chaque évaluation figée <strong>recopie</strong> le paramétrage du jour :
        changer un réglage ne réécrit aucune évaluation passée.
      </p>

      <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}>
        {nombre('fenetreMois', 'Fenêtre de référence, en mois',
          'La période sur laquelle les points de contrôle sont pris. Plus courte que le rythme de révision, elle ferait publier une incertitude établie sur une saison.')}
        {nombre('minPoints', 'Points minimaux par niveau',
          'En deçà, l’écart-type est trop instable pour porter une incertitude publiée, et aucun chiffre n’est rendu — plutôt qu’un chiffre fragile.')}
        {nombre('k', 'Facteur d’élargissement k',
          'k = 2 ≈ niveau de confiance 95 %. C’est ce que retient le mémoire du laboratoire et ce qu’attend le formulaire TUNAC.', '0.1')}
        <div>
          <label style={lbl} htmlFor="p-biais">Traitement du biais</label>
          <select id="p-biais" value={v.traitementBiais}
            onChange={(e) => setV((x) => ({ ...x, traitementBiais: e.target.value }))} style={inputStyle}>
            {d.traitementsBiais.map((t) => <option key={t.cle} value={t.cle}>{t.libelle}</option>)}
          </select>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
            {d.traitementsBiais.find((t) => t.cle === v.traitementBiais)?.formule}
          </p>
        </div>
        <div>
          <label style={lbl} htmlFor="p-fam">Colonne du référentiel qui juge</label>
          <select id="p-fam" value={v.familleCritere}
            onChange={(e) => setV((x) => ({ ...x, familleCritere: e.target.value }))} style={inputStyle}>
            {d.famillesCritere.map((f) => <option key={f.cle} value={f.cle}>{f.libelle}</option>)}
          </select>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
            {d.famillesCritere.find((f) => f.cle === v.familleCritere)?.commentaire}
          </p>
        </div>
        {nombre('facteurLimite', 'Élargissement du seuil du référentiel',
          'DÉCISION DU LABORATOIRE, 07/08/2026 (question Q1) : la limite du référentiel s’applique TELLE QU’ELLE Y FIGURE, sans élargissement — donc 1. Le mémoire annonce un ×2 « pour un risque de 5 % », mais ses propres chiffres ne le suivent pas (±10 = 2×5, ±4 = 1×4) ; élargir rendrait conformes des méthodes que la source déclare hors limites.', '0.1')}
        <div>
          <label style={lbl} htmlFor="p-ambigu">Rattachement de niveau AMBIGU</label>
          <select id="p-ambigu" value={v.rattachementAmbigu}
            onChange={(e) => setV((x) => ({ ...x, rattachementAmbigu: e.target.value }))} style={inputStyle}>
            {d.rattachementsAmbigus.map((r) => <option key={r.cle} value={r.cle}>{r.libelle}</option>)}
          </select>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
            {d.rattachementsAmbigus.find((r) => r.cle === v.rattachementAmbigu)?.commentaire}
          </p>
        </div>
        {nombre('seuilSurveillancePct', 'Bascule « à surveiller », en % du seuil',
          'Une incertitude qui consomme plus de cette part de l’erreur totale admissible n’est pas non conforme, mais elle n’a plus de marge.')}
        {nombre('periodiciteRevueMois', 'Rythme de révision, en mois',
          'Le mémoire du laboratoire écrit « annuellement, ou sur événement ». L’échéance est dérivée de la date d’évaluation, jamais stockée.')}
        {nombre('kEtalon', 'k supposé des certificats d’étalonnage',
          'Les certificats accrédités publient une incertitude élargie ; elle est ramenée à u en la divisant par ce facteur.', '0.1')}
      </div>

      <PiedActions taille="sm" annuler={{ onClick: onAnnule }} principale={{ libelle: "Enregistrer le mode de calcul", disabled: estOccupe('p'), onClick: enregistrer }} />
      <FieldError>{erreur}</FieldError>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * L'écran
 * ═══════════════════════════════════════════════════════════════════════════ */

export function Incertitude() {
  const [d, setD] = useState<GedIncDossier | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(false);
  const [tour, setTour] = useState(0);
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('tous');
  const [ouvert, setOuvert] = useState('');
  const [regle, setRegle] = useState(false);

  useEffect(() => {
    let vivant = true;
    // Pas de `setChargement(true)` ici : `react-hooks/set-state-in-effect` est
    // une ERREUR de lint dans ce dépôt, et le rechargement conserve de toute
    // façon la liste précédente à l'écran plutôt que de la faire clignoter.
    void fetch('/api/ged/ged/incertitudes', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('lecture refusée'))))
      .then((j: GedIncDossier) => { if (vivant) { setD(j); setErreur(false); } })
      // Un écran vide parce que la lecture a échoué se lirait « aucun examen
      // n'a d'incertitude » — c'est-à-dire un constat d'audit fabriqué par une
      // panne réseau. On le refuse.
      .catch(() => { if (vivant) { setD(null); setErreur(true); } })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [tour]);

  const filtrees = useMemo(() => {
    const base = (d?.lignes || []).filter((l) => {
      if (filtre === 'complete') return l.couverture === 'complete';
      if (filtre === 'partielle') return l.couverture === 'partielle';
      if (filtre === 'aucune') return l.couverture === 'aucune';
      if (filtre === 'sansref') return !l.ligneRefTrouvee;
      if (filtre === 'jamais') return !l.derniere;
      if (filtre === 'retard') return !!l.derniere?.enRetard;
      if (filtre === 'nc') return l.verdict === 'non_conforme';
      return true;
    });
    const qq = q.trim().toLowerCase();
    return qq
      ? base.filter((l) => `${l.analyteCode} ${l.analyteNom} ${l.equipementCode} ${l.equipementNom} ${l.discipline} ${l.matiere}`.toLowerCase().includes(qq))
      : base;
  }, [d, filtre, q]);
  const pages = usePagination(filtrees);

  if (chargement) return <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Calcul des incertitudes de mesure…</p>;
  if (erreur || !d) {
    return (
      <p style={{ fontSize: 13, color: 'var(--red-600)' }}>
        Couverture indisponible — le serveur n’a pas répondu. Ce n’est <strong>pas</strong> « aucun examen
        n’a d’incertitude » : réessayez.
      </p>
    );
  }

  const b = d.bilan;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <H1 eyebrow={<>Processus analytique · <RefISO code="7.3.4" /><Norme art="7.3.4" /></>}
        title="Incertitude de mesure" />

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '88ch' }}>
        L’incertitude n’est <strong>jamais saisie</strong> : elle est calculée sur les points de contrôle
        interne (reproductibilité intra-laboratoire), sur l’écart aux valeurs assignées des lots de
        contrôle (biais), et sur l’incertitude de l’étalon de la chaîne de raccordement quand il existe.
        Un chiffre ressaisi serait faux dès la première dérive de la méthode, et personne ne le verrait.
        Ce que le laboratoire ne peut pas calculer est affiché comme tel, <strong>nommément</strong>,
        plutôt que rendu sous forme d’un chiffre bâti sur du vide.
      </p>

      {ouvert ? (
        <Detail id={ouvert} onFerme={() => setOuvert('')} onEvalue={() => setTour((t) => t + 1)} />
      ) : (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Tuile n={b.cartes} label="Examens contrôlés" aide="Cartes de contrôle interne actives : un paramètre sur un automate." />
            <Tuile n={b.complete} label="Couverture complète" aide="Les trois composantes sont disponibles : reproductibilité, biais, et incertitude de la valeur de référence." />
            <Tuile n={b.partielle} label="Couverture partielle" aide="Reproductibilité et biais sont là, l’incertitude de la valeur de référence manque. Le chiffre est rendu, mais il est sous-évalué." />
            <Tuile n={b.aucune} label="Aucune incertitude" ton="alerte" aide="Il manque la reproductibilité ou le biais : il n’y a pas d’incertitude, et aucun chiffre n’est rendu." />
            <Tuile n={b.figees} label="Évaluations figées" ton="sourd" aide="Un calcul existe pour bien plus d’examens que cela — mais tant qu’il n’est pas figé, daté et signé, il n’est pas opposable." />
            <Tuile n={b.enRetardDeRevue} label="Révisions échues" ton={b.enRetardDeRevue ? 'alerte' : 'sourd'} aide="La norme demande une révision périodique. L’échéance est dérivée de la date d’évaluation et du rythme paramétré." />
          </div>

          {b.sansEtalon === b.cartes && b.cartes > 0 && (
            <div style={encart}>
              <strong style={{ color: 'var(--text-strong)' }}>
                Aucun examen n’a de couverture complète, et la raison est unique.
              </strong>{' '}
              Le registre des étalons et matériaux de référence (§6.5) est <strong>vide</strong> : la
              troisième composante — l’incertitude de la valeur assignée — est inconnue pour les
              {' '}{b.cartes} examens. Elle n’a <em>pas</em> été remplacée par zéro, mais toutes les
              incertitudes affichées sont par conséquent <strong>sous-évaluées</strong>. Alimenter la
              chaîne de raccordement est ce qui les rendra complètes.
            </div>
          )}

          {b.sansReferentiel > 0 && (
            <div style={encart}>
              <strong style={{ color: 'var(--text-strong)' }}>
                {b.sansReferentiel} examen{b.sansReferentiel > 1 ? 's' : ''} sans critère d’acceptabilité.
              </strong>{' '}
              Le référentiel de critères de performance ne porte aucune ligne pour eux : leur incertitude
              peut être calculée, mais elle n’est comparée à rien, et aucun verdict n’est rendu.
              « Sans verdict » n’est pas « conforme ».
            </div>
          )}

          {d.peutEvaluer && !regle && (
            <div>
              <Bouton taille="sm" onClick={() => setRegle(true)}>
                <I n="gear" /> Mode de calcul
              </Bouton>
              {/* Ce qui est tracé se VOIT (24/08/2026). */}
              <span style={{ marginInlineStart: 8 }}><BoutonJournal reference="INCERTITUDE-PARAM" quoi="du mode de calcul" /></span>
              <span style={{ marginInlineStart: 10, fontSize: 12.5, color: 'var(--text-muted)' }}>
                fenêtre {d.parametrage.fenetreMois} mois · k = {d.parametrage.k} · biais «{' '}
                {d.parametrage.traitementBiais} » · seuils « {d.parametrage.familleCritere} »
                {d.parametrage.defini ? '' : ' (valeurs par défaut, jamais réglées)'}
              </span>
            </div>
          )}

          {regle && d.peutEvaluer && (
            <Parametrage d={d} onFait={() => { setRegle(false); setTour((t) => t + 1); }}
              onAnnule={() => setRegle(false)} />
          )}

          <FilterBar
            q={q} onQ={setQ} placeholder="Rechercher un paramètre, un automate, une discipline…"
            chips={[
              { k: 'tous', label: 'Tous', n: b.cartes },
              { k: 'complete', label: 'Complète', n: b.complete },
              { k: 'partielle', label: 'Partielle', n: b.partielle },
              { k: 'aucune', label: 'Aucune incertitude', n: b.aucune },
              { k: 'sansref', label: 'Sans critère', n: b.sansReferentiel },
              { k: 'jamais', label: 'Jamais figées', n: b.cartes - b.figees },
              { k: 'nc', label: 'Non conformes', n: b.nonConforme },
            ]}
            actif={filtre} onChip={setFiltre}
            total={b.cartes} affiches={filtrees.length}
            onReset={() => { setQ(''); setFiltre('tous'); }} />

          {filtrees.length === 0 ? (
            <EmptyState titre="Aucun examen ne correspond à ce filtre."
              indice="Élargissez la recherche : la couverture porte sur les cartes de contrôle interne actives." />
          ) : (
            <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
              {pages.visibles.map((l, i) => <Ligne key={l.documentId} l={l} premier={i === 0} onOuvrir={() => setOuvert(l.documentId)} />)}
            </div>
          )}
          {/* 126 blocs sur dix-neuf écrans (relevé du 27/08/2026, P2). */}
          <Pagination page={pages.page} pages={pages.pages} debut={pages.debut} fin={pages.fin}
            total={pages.total} onPage={pages.setPage} quoi="examens" />
        </>
      )}
    </div>
  );
}

function Ligne({ l, premier, onOuvrir }: { l: GedIncLigne; premier: boolean; onOuvrir: () => void }) {
  return (
    <div style={{
      padding: '10px 13px', borderTop: premier ? 'none' : '1px solid var(--border-subtle)',
      display: 'grid', gap: 4,
    }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Bouton taille="sm" onClick={onOuvrir}>
          {l.analyteCode || l.analyteNom || '—'}
        </Bouton>
        <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>{l.analyteNom}</span>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {l.equipementCode}{l.matiere ? ` · ${l.matiere}` : ''}{l.unite ? ` · ${l.unite}` : ''}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ ...mono, color: 'var(--text-muted)' }}>
          {l.nbPoints} pt · {l.nbLots} lot{l.nbLots > 1 ? 's' : ''}
        </span>
        {l.uPct.some((u) => u !== null) && (
          <span style={{ ...mono, fontWeight: 700, color: 'var(--text-strong)' }}>
            U = {l.uPct.map((u) => (u === null ? '—' : `± ${NB(u, 1)} %`)).join(' / ')}
          </span>
        )}
        <Couv c={l.couverture} />
        <Verdict v={l.verdict} />
      </div>
      {l.derniere ? (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Figée {l.derniere.ref} le {fmtDate(l.derniere.dateEvaluation)}
          {/* ⚠️ MÊME PIÈGE qu'en tête de ce fichier, à un second endroit que le
              correctif d'origine avait manqué : le parseur du serveur de
              DÉVELOPPEMENT rejette `… : <>…</>}` en fin de ternaire. `next build`
              l'accepte et `tsc` aussi — le défaut ne se voit donc qu'à l'écran,
              sous la forme d'un 500 sur toute l'application en développement.
              Un <span> nommé lève l'ambiguïté sans rien changer au rendu. */}
          {l.derniere.enRetard
            ? <strong style={{ color: 'var(--red-600)' }}> — révision échue le {fmtDate(l.derniere.revuePrevue)}</strong>
            : <span> · révision prévue le {fmtDate(l.derniere.revuePrevue)}</span>}
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Aucune évaluation figée : le calcul existe, il n’est pas opposable.
        </div>
      )}
      {l.manques.length > 0 && (
        <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 12.5, color: 'var(--amber-600)', lineHeight: 1.5 }}>
          {l.manques.slice(0, 3).map((m) => <li key={m}>{m}</li>)}
          {l.manques.length > 3 && <li style={{ color: 'var(--text-muted)' }}>… et {l.manques.length - 3} autre(s).</li>}
        </ul>
      )}
    </div>
  );
}
